import React, { useState } from 'react'
import { Button, CircularProgress, useTheme } from '@material-ui/core'
import { toast } from 'react-toastify'
import { Container } from '../../components'
import { axiosInstance } from '../../helpers'
import { Invoice } from '../../models'
import { typographyStyle } from '../../theme/typographyStyle'

interface Props {
    invoice: Invoice
    showActions?: boolean
    onUpdated?: () => void
}

export const VendorInvoiceRequestActions = (props: Props) => {
    const { invoice, showActions = true, onUpdated } = props
    const theme = useTheme()
    const [actionInvoiceId, setActionInvoiceId] = useState<number | null>(null)
    const [actionType, setActionType] = useState<'confirm' | 'deny' | null>(
        null,
    )

    const status = invoice.apartment_initiated_status
    if (!status) {
        return null
    }

    const confirmInvoice = () => {
        setActionInvoiceId(invoice.id)
        setActionType('confirm')
        axiosInstance
            .post(`invoice/${invoice.id}/vendor_confirm/`)
            .then(() => {
                toast.success('Invoice confirmed successfully')
                onUpdated?.()
            })
            .catch((e) => {
                toast.error(
                    e.response?.data?.message ?? 'Failed to confirm invoice',
                )
            })
            .finally(() => {
                setActionInvoiceId(null)
                setActionType(null)
            })
    }

    const denyInvoice = () => {
        setActionInvoiceId(invoice.id)
        setActionType('deny')
        axiosInstance
            .post(`invoice/${invoice.id}/vendor_deny/`)
            .then(() => {
                toast.success('Invoice denied')
                onUpdated?.()
            })
            .catch((e) => {
                toast.error(
                    e.response?.data?.message ?? 'Failed to deny invoice',
                )
            })
            .finally(() => {
                setActionInvoiceId(null)
                setActionType(null)
            })
    }

    const statusLabel =
        status === 'PENDING'
            ? 'Pending'
            : status === 'CONFIRMED'
            ? 'Confirmed'
            : 'Denied'

    const statusColor =
        status === 'CONFIRMED'
            ? theme.palette.success.light
            : status === 'DENIED'
            ? theme.palette.error.light
            : theme.palette.grey[300]

    return (
        <Container style={{ alignItems: 'center' }}>
            <span
                style={{
                    display: 'inline-block',
                    minWidth: 80,
                    padding: theme.spacing(0.25, 1),
                    borderRadius: theme.shape.borderRadius,
                    backgroundColor: statusColor,
                    ...typographyStyle(theme.typography.caption),
                    fontWeight: 600,
                    textAlign: 'center',
                }}
            >
                {statusLabel}
            </span>
            {showActions && status === 'PENDING' && invoice.total_cost > 0 && (
                <Container
                    style={{
                        marginLeft: theme.spacing(1),
                        display: 'flex',
                        gap: theme.spacing(1),
                    }}
                >
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        disabled={actionInvoiceId === invoice.id}
                        onClick={(e) => {
                            e.stopPropagation()
                            confirmInvoice()
                        }}
                        style={{ textTransform: 'none' }}
                    >
                        {actionInvoiceId === invoice.id &&
                        actionType === 'confirm' ? (
                            <CircularProgress size={14} color="inherit" />
                        ) : (
                            'Confirm'
                        )}
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        disabled={actionInvoiceId === invoice.id}
                        onClick={(e) => {
                            e.stopPropagation()
                            denyInvoice()
                        }}
                        style={{ textTransform: 'none' }}
                    >
                        {actionInvoiceId === invoice.id &&
                        actionType === 'deny' ? (
                            <CircularProgress size={14} color="inherit" />
                        ) : (
                            'Deny'
                        )}
                    </Button>
                </Container>
            )}
        </Container>
    )
}
